import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import croonusLogo from "../../assets/images/logo/croonus-logo-powered.png";
import Sitemap from "../../routes/sitemap";
import useAPI from "../../hooks/useAPI";
import AuthContext from "store/auth-context";
import { regax } from "helpers/const";
import useInput from "hooks/use-input";
import FrogotPasword from "components/ForgotPassword/ForgotPassword";
import scss from "./LoginPage.module.scss";
import { useTranslation } from "react-i18next";
import { printError } from "../../helpers/apiError";

import { splitSubtitle } from "../../helpers/authPresentation";

/**
 * The login page for the B2B.
 *
 * @return {JSX.Element}
 * @constructor
 */
const LoginPage = () => {
    const { t } = useTranslation();

    const api = useAPI();
    const { login } = useContext(AuthContext);
    const navigate = useNavigate();

    const {
        value: emailValue,
        isValid: emailIsValid,
        hasError: emailHasError,
        valueChangeHandler: emailChangeHandler,
        inputBlurHandler: emailBlurHandler,
    } = useInput((value) => (!value && value.trim() !== "") || regax.test(value) !== false);

    const {
        value: passwordValue,
        isValid: passwordIsValid,
        hasError: passwordHasError,
        valueChangeHandler: passwordChangeHandler,
        inputBlurHandler: passwordBlurHandler,
    } = useInput((value) => value.trim() !== "");

    const [forgotPasswordDialog, setForgotPasswordDialog] = useState(false);

    /** Handle form submitting. */
    const submitHandler = async (event) => {
        event.preventDefault();

        // Guard: supplied values are valid
        if (!(emailIsValid && passwordIsValid)) {
            toast.warning(t("login-default-form-message-check_fields"));
            return;
        }

        // Prepare the data
        const loginData = {
            email: emailValue,
            password: passwordValue,
        };

        // Execute the API
        api.post("/customer/login", loginData)
            .then((response) => {
                login(response);
                localStorage.setItem("currency", response?.price_market?.currency);
                navigate("/pocetna");
            })
            .catch((error) => {
                console.error(error);
                toast.error(printError(error) ? printError(error) : t("global-message-unknown_error"));
            });
    };

    // The title, subtitle and the description
    const [title, setTitle] = useState("");
    const [subtitle, setSubtitle] = useState("");
    const [description, setDescription] = useState("");
    useEffect(() => {
        api.get("/configuration/presentation/login/title/value", false).then((response) => setTitle(response));
        api.get("/configuration/presentation/login/subtitle/value", false).then((response) => setSubtitle(response));
        api.get("/configuration/presentation/login/description/value", false).then((response) => setDescription(response));
    }, [api]);
    const subtitleLines = splitSubtitle(subtitle);

    return (
        <>
            <div className={scss["login-container"]}>
                <div className={`${scss["login-card"]} ${scss.compactCard}`}>
                    <div className={scss["background-wrapper"]}>
                        <img src={api.buildPath("/configuration/images/presentation/login/image")} alt="" />
                        <div />
                    </div>
                    <div className={scss["login-card-gray-overlay"]}>
                        {/* Background image */}
                        <div className={scss.logoHolder}>
                            <img
                                src={api.buildPath("/configuration/images/presentation/login/logo")}
                                className={scss.logo}
                                alt="Logo kompanije"
                            />
                            <p className={scss.logoText} title={subtitle}>
                                <span className={scss.srOnly}>{subtitle}</span>
                                {subtitleLines.map((line, index) => (
                                    <span key={index} aria-hidden="true">{line}</span>
                                ))}
                            </p>
                        </div>

                        {/* Title, subtitle and description */}
                        <div className={scss.intro}>
                            <h1 className={scss.introTitle}>{title}</h1>
                        </div>
                        <p className={`${scss.introDescription} col-xxl-4 col-lg-6`}>{description}</p>

                        <form onSubmit={submitHandler} className={scss["form"] + " col-xxl-4 col-lg-6 login-form-style"}>
                            <div className={scss["input-container"]}>
                                <label className={scss.srOnly} htmlFor="email">
                                    {t("login-default-form-field-username-label")}
                                </label>
                                <input
                                    id="email"
                                    type="text"
                                    inputMode="email"
                                    autoComplete="username"
                                    value={emailValue}
                                    onChange={emailChangeHandler}
                                    onBlur={emailBlurHandler}
                                    placeholder={t("login-default-form-field-username-label")}
                                    aria-invalid={emailHasError}
                                    aria-describedby="email-error"
                                    className={`${scss.input} ${emailHasError ? scss.inputInvalid : ""}`}
                                />
                                <p id="email-error" className={`${scss.validation} ${emailHasError ? scss.validationVisible : ""}`} aria-live="polite">
                                    {emailHasError ? t("login-default-form-field-username-error-required") : ""}
                                </p>
                            </div>
                            <div className={scss["input-container"]}>
                                <label className={scss.srOnly} htmlFor="password">
                                    {t("login-default-form-field-password-label")}
                                </label>
                                <input
                                    id="password"
                                    type="password"
                                    autoComplete="current-password"
                                    value={passwordValue}
                                    onChange={passwordChangeHandler}
                                    onBlur={passwordBlurHandler}
                                    placeholder={t("login-default-form-field-password-label")}
                                    aria-invalid={passwordHasError}
                                    aria-describedby="password-error"
                                    className={`${scss.input} ${passwordHasError ? scss.inputInvalid : ""}`}
                                />
                                <p id="password-error" className={`${scss.validation} ${passwordHasError ? scss.validationVisible : ""}`} aria-live="polite">
                                    {passwordHasError ? t("login-default-form-field-password-error-required") : ""}
                                </p>
                            </div>
                            <div className={scss["login-submit-container"]}>
                                <button type="submit" className={scss["login-submit"]}>
                                    {t("login-default-form-button-submit")}
                                </button>
                            </div>
                        </form>
                        <div className={`${scss.register} col-xxl-4 col-lg-6`}>
                            <a href={Sitemap.auth.register}>{t("login-default-text-become_user")}.</a>
                            <button type="button" className={scss.forgotPassword} onClick={() => setForgotPasswordDialog(true)}>
                                {t("login-default-text-forgot_password")}
                            </button>
                        </div>

                        {/* Copyright */}
                        <div className={scss["powered"] + " col-xxl-4 col-lg-6"}>
                            <p className={scss["paragraph"]}>{t("login-default-text-powered_by")}: </p>
                            <a href="https://croonus.com/" target="_blank" rel="noopener noreferrer">
                                <img src={croonusLogo} alt="Croonus logo" />
                            </a>
                        </div>
                    </div>
                </div>
            </div>
            <FrogotPasword openDialog={forgotPasswordDialog} setOpenDialog={setForgotPasswordDialog} enteredEmail={emailValue} translateKey={"login-forgot_password"} />
        </>
    );
};

export default LoginPage;
